import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { APPROVAL_STATUS, NODE_TYPE } from '../../services/workflowService';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { tasksI18n } from '../Tasks/i18n';
import './approvalFlowModal.scss';

const APPROVER_TYPE = {
  USER: 1,
  ROLE: 2,
  GROUP: 3,
};

const parseEnumNumber = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toLowerCase();
  if (normalized === 'task') return NODE_TYPE.TASK;
  if (normalized === 'approval') return NODE_TYPE.APPROVAL;
  if (normalized === 'pending') return APPROVAL_STATUS.PENDING;
  if (normalized === 'approved') return APPROVAL_STATUS.APPROVED;
  if (normalized === 'rejected') return APPROVAL_STATUS.REJECTED;
  if (normalized === 'user') return APPROVER_TYPE.USER;
  if (normalized === 'role') return APPROVER_TYPE.ROLE;
  if (normalized === 'group') return APPROVER_TYPE.GROUP;

  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
};

const formatDateWithTime = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hour = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hour}:${minutes}`;
};

const resolveApprover = (node, t) => {
  const approverType = parseEnumNumber(node.approverType);

  if (approverType === APPROVER_TYPE.USER) {
    return t('flow_approver_user', { id: node.requiredApproverUserId ?? '-' });
  }

  if (approverType === APPROVER_TYPE.ROLE) {
    return t('flow_approver_role', { id: node.requiredApproverRoleId ?? '-' });
  }

  if (approverType === APPROVER_TYPE.GROUP) {
    return t('flow_approver_group', { id: node.requiredApproverGroupId ?? '-' });
  }

  return t('flow_approver_undefined');
};

const resolveNodeStatus = (node) => {
  const nodeType = parseEnumNumber(node.nodeType);
  const approvalStatus = parseEnumNumber(node.approvalStatus);
  const workflowNodeStatus = parseEnumNumber(node.status);

  if (nodeType === NODE_TYPE.APPROVAL) {
    if (approvalStatus === APPROVAL_STATUS.APPROVED) {
      return 'approved';
    }

    if (approvalStatus === APPROVAL_STATUS.REJECTED) {
      return 'rejected';
    }
  }

  if (workflowNodeStatus === 3) {
    return 'approved';
  }

  if (workflowNodeStatus === 4) {
    return 'rejected';
  }

  if (workflowNodeStatus === 2) {
    return 'active';
  }

  return 'pending';
};

const resolveNodeDecisionTotals = (node) => {
  const completedRaw =
    node?.approvalCompletedCount ??
    node?.approvalcompletedcount ??
    0;
  const totalRaw =
    node?.approvalTotalCount ??
    node?.approvaltotalcount ??
    0;

  const completed = Number.isFinite(Number(completedRaw))
    ? Number(completedRaw)
    : 0;
  const total = Number.isFinite(Number(totalRaw)) ? Number(totalRaw) : 0;

  if (total > 0) {
    return { completed, total };
  }

  // Fallback for legacy payloads without aggregated counters.
  const status = resolveNodeStatus(node);
  return {
    completed: status === 'approved' ? 1 : 0,
    total: 1,
  };
};

const ApprovalFlowModal = ({
  DesignSystemComponents,
  show,
  flow,
  request,
  onClose,
}) => {
  const { Modal } = DesignSystemComponents;

  const { language } = useLanguage();
  const tasksLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: tasksI18n });

  useEffect(() => {
    tasksI18n.changeLanguage(tasksLanguage);
  }, [tasksLanguage]);

  const nodes = Array.isArray(flow?.nodes) ? flow.nodes : [];
  const orderedSteps = [...nodes].sort((left, right) => {
    const leftOrder = Number(left?.sequenceOrder || 0);
    const rightOrder = Number(right?.sequenceOrder || 0);
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return String(left?.name || '').localeCompare(String(right?.name || ''));
  });

  const approvalSteps = orderedSteps.filter(
    (node) => parseEnumNumber(node.nodeType) === NODE_TYPE.APPROVAL
  );

  const progressTotals = approvalSteps.reduce(
    (acc, node) => {
      const { completed, total } = resolveNodeDecisionTotals(node);
      acc.completed += completed;
      acc.total += total;
      return acc;
    },
    { completed: 0, total: 0 }
  );

  const completedSteps = progressTotals.completed;
  const totalSteps = progressTotals.total;
  const pendingSteps = Math.max(totalSteps - completedSteps, 0);
  const progressPercent =
    totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  if (!Modal) return null;

  return (
    <Modal
      title={request?.task || flow?.workflowName || t('flow_modal_title_fallback')}
      show={show}
      onClose={onClose}
      primaryBtnTxt={t('flow_btn_close')}
      primaryBtnAction={onClose}
      secondaryBtnTxt={undefined}
      width={700}
    >
      <div className='approval-flow-modal'>
        <p className='approval-flow-modal__subtitle mb-3'>
          Workflow: {request?.workflow || flow?.workflowName || '-'}
        </p>

        <div className='approval-flow-modal__summary'>
          <div className='approval-flow-modal__summary-item'>
            <div className='mini color-gray-500 mb-1'>{t('flow_label_requester')}</div>
            <div className='caption'>{request?.requester || '-'}</div>
          </div>
          <div className='approval-flow-modal__summary-item'>
            <div className='mini color-gray-500 mb-1'>{t('flow_label_request_date')}</div>
            <div className='caption'>{request?.date || '-'}</div>
          </div>
          <div className='approval-flow-modal__summary-item'>
            <div className='mini color-gray-500 mb-1'>{t('flow_label_priority')}</div>
            <span className='approval-flow-modal__priority'>
              {request?.priority || t('flow_priority_fallback')}
            </span>
          </div>
        </div>

        <h4 className='fw-semibold mb-3'>{t('flow_section_title')}</h4>

        <div className='approval-flow-modal__steps'>
          {approvalSteps.map((node, index) => {
            const stepStatus = resolveNodeStatus(node);
            const statusClass = `approval-flow-step approval-flow-step--${stepStatus}`;
            const statusSymbol = stepStatus === 'approved'
              ? 'v'
              : stepStatus === 'rejected'
                ? 'x'
                : stepStatus === 'active'
                  ? '>'
                  : 'o';
            const statusDate = formatDateWithTime(node.completedAtUtc);

            return (
              <div className={statusClass} key={node.instanceNodeId || index}>
                <span className='approval-flow-step__icon'>{statusSymbol}</span>

                <div>
                  <h5 className='fw-semibold mb-1'>
                    {t('flow_step_label', { number: index + 1 })}: {node.name || t('flow_step_name_fallback')}
                  </h5>
                  <p className='caption font-light mb-1'>{resolveApprover(node, t)}</p>
                  {stepStatus === 'active' && (
                    <p className='mini color-primary-500 mb-0'>{t('flow_step_active')}</p>
                  )}
                  {stepStatus === 'approved' && statusDate && (
                    <p className='mini color-gray-500 mb-0'>
                      {t('flow_step_completed_date', { date: statusDate })}
                    </p>
                  )}
                  {stepStatus === 'rejected' && statusDate && (
                    <p className='mini color-error-500 mb-0'>
                      {t('flow_step_rejected_date', { date: statusDate })}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className='approval-flow-modal__progress'>
          <div>
            <p className='fw-semibold mb-1'>
              {t('flow_progress_completed', { completed: completedSteps, total: totalSteps })}
            </p>
            <p className='mini mb-0'>
              {t('flow_progress_pending', { pending: pendingSteps })}
            </p>
          </div>
          <div className='approval-flow-modal__progress-value'>
            {progressPercent}%
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ApprovalFlowModal;
